/**
 * Interactive onboarding tour with tooltips
 */

import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TourStep } from '@/hooks/useOnboarding';

type Props = {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
};

export function OnboardingTour({ 
  step, 
  currentStep, 
  totalSteps, 
  onNext, 
  onPrevious, 
  onSkip 
}: Props) {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const targetElement = document.querySelector(step.target);
    
    if (targetElement && tooltipRef.current) {
      // Highlight target element
      targetElement.classList.add('onboarding-highlight');
      
      // Position tooltip near target
      const rect = targetElement.getBoundingClientRect();
      const tooltip = tooltipRef.current;
      
      let top = 0;
      let left = 0;
      
      switch (step.placement || 'bottom') {
        case 'top':
          top = rect.top - tooltip.offsetHeight - 16;
          left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
          break;
        case 'bottom':
          top = rect.bottom + 16;
          left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
          break;
        case 'left':
          top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
          left = rect.left - tooltip.offsetWidth - 16;
          break;
        case 'right':
          top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
          left = rect.right + 16;
          break;
      }
      
      // Keep tooltip within viewport
      top = Math.max(16, Math.min(top, window.innerHeight - tooltip.offsetHeight - 16));
      left = Math.max(16, Math.min(left, window.innerWidth - tooltip.offsetWidth - 16));
      
      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
      
      // Scroll target into view
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    return () => {
      const targetElement = document.querySelector(step.target);
      targetElement?.classList.remove('onboarding-highlight');
    };
  }, [step]);

  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 animate-fade-in" />
      
      {/* Tooltip */}
      <Card 
        ref={tooltipRef}
        className="fixed z-50 w-[400px] shadow-elegant animate-scale-in"
      >
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary">
                Step {currentStep + 1} of {totalSteps}
              </Badge>
            </div>
            <CardTitle className="text-lg">{step.title}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onSkip}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.content}
          </p>
        </CardContent>
        
        <CardFooter className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={onPrevious}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onSkip}>
              Skip Tour
            </Button>
            <Button size="sm" onClick={onNext}>
              {isLast ? 'Finish' : 'Next'}
              {!isLast && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </>
  );
}
